import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Tooltip } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ImportRowEditHandler } from '../../importRows';
import { RowExclusionLockContext } from '../rowExclusion';

type RowEditButtonProps = ImportRowEditHandler & {
    rowNumber: number;
};

/**
 * A row's visible Edit, beside its Exclude / Include in the table's last
 * column and at the top of a phone card: a ghost icon-only button (a pencil)
 * with a tooltip that opens the row in the drawer, as pressing the row does.
 * While the import runs (`RowExclusionLockContext`) it waits, as the row's
 * other control does.
 *
 * The tooltip's trigger drops an `aria-label`, so the button is named by a
 * visually hidden span that also names the row ("Edit row 902").
 */
export const RowEditButton: React.FC<RowEditButtonProps> = ({
    rowNumber,
    onEditRow,
}) => {
    const { t } = useTranslation('pages');
    const isLocked = React.useContext(RowExclusionLockContext);

    return (
        <Tooltip delay={0}>
            <Button
                isIconOnly
                isDisabled={isLocked}
                onPress={() => onEditRow(rowNumber)}
                size="sm"
                variant="ghost"
                {...getNodeTestIdProp(`catalog-import-row-edit-${rowNumber}`)}
            >
                <Icon aria-hidden icon="gravity-ui:pencil" />
                <span className="sr-only">
                    {t('catalogImport.products.rowAction.editRow', {
                        row: rowNumber,
                    })}
                </span>
            </Button>
            <Tooltip.Content>
                <p>{t('catalogImport.products.rowAction.edit')}</p>
            </Tooltip.Content>
        </Tooltip>
    );
};
